import { useCallback, useEffect, useState } from "react";
import { FormBuilder } from "./form-builder";
import { FormBuilderOptions, ValidationResult } from "./form-builder.model";
import Modal from "../modal/modal";
import { Box, Button, Typography } from "@mui/material";
import { validateFormItem } from "./helper";
import { showForm } from "./form-builder-signal";
import { isEmpty } from "lodash";

/**
 * Form Builder Manager Component. Listens for showForm events and displays
 * a modal with the form builder when triggered.
 * @returns JSX.Element
 */
export const FormBuilderManager: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentFormProps, setCurrentFormProps] =
    useState<FormBuilderOptions>();

  /**
   * Subscribe to showForm events on component mount
   */
  useEffect(() => {
    const sub = showForm.subscribe((onNext: FormBuilderOptions | undefined) => {
      if (!onNext) {
        setValues({});
        setInputErrors({});
        setCurrentFormProps(undefined);
        setIsOpen(false);
        return;
      }
      setCurrentFormProps(onNext);
      setValues(onNext.initialValues || {});
      setInputErrors({});
      setIsOpen(true);
    });

    return () => sub.unsubscribe();
  }, []);

  /**
   * State for form values
   */
  const [values, setValues] = useState<Record<string, unknown>>(
    (): Record<string, unknown> => currentFormProps?.initialValues || {},
  );
  /**
   * State for input errors
   */
  const [inputErrors, setInputErrors] = useState<
    Record<string, ValidationResult[] | undefined>
  >({});

  /**
   * Validate and submit the form
   * @param {boolean} submitIfNoErrors boolean indicating whether to submit if no errors found
   */
  const validateAndSubmit = useCallback(
    (submitIfNoErrors: boolean) => {
      if (!currentFormProps) return;
      const errors: Record<string, ValidationResult[] | undefined> = {};
      currentFormProps.formItems.forEach((fi) => {
        const validationMsg = validateFormItem(fi, values, currentFormProps);
        if (validationMsg) {
          errors[fi.name] = validationMsg;
        } else {
          delete errors[fi.name];
        }
      });

      setInputErrors(errors);

      if (isEmpty(errors) && submitIfNoErrors && currentFormProps.onSubmit) {
        currentFormProps.onSubmit(values, () => setIsOpen(false));
      } else if (submitIfNoErrors && currentFormProps.onSubmit) {
        // there are errors, do not submit
        console.log("Do not submit form due to errors:", errors);
      } else if (submitIfNoErrors && !currentFormProps.onSubmit) {
        console.log("No onSubmit handler defined for form.");
      }
    },
    [currentFormProps, values],
  );

  return isOpen && currentFormProps ? (
    <Modal
      id="form-builder-manager"
      panelId="form-builder-manager"
      scrollContent={true}
      onClose={() => setIsOpen(false)}
      header={currentFormProps.title || "Form Builder"}
      initialSize={{ width: "400px", height: "auto" }}
      modalOnly={true}
      footer={
        <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 2, pt: 0.5 }}>
          <Button variant="contained" onClick={() => validateAndSubmit(true)}>
            {currentFormProps.submitLabel ?? "Submit"}
          </Button>
          {currentFormProps.showValidateButton && (
            <Button onClick={() => validateAndSubmit(false)}>Validate</Button>
          )}
        </Box>
      }
    >
      <>
        {currentFormProps.subtitle && (
          <Typography variant="body2" sx={{ px: 2, pt: 1 }}>
            {currentFormProps.subtitle}
          </Typography>
        )}
        <FormBuilder
          formBuilderOpts={currentFormProps}
          values={values}
          setValues={setValues}
          inputErrors={inputErrors}
          setInputErrors={setInputErrors}
        />
      </>
    </Modal>
  ) : (
    <></>
  );
};
